import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";

import {
  MDSCard,
  MDSCardVariant,
  MDSIconKind,
  MDSSkeleton,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
} from "@/mds";
import { AgentHorizontalCardCarousel } from "@/pages/agent/components/agent-horizontal-card-carousel";
import { AgentPageLayout } from "@/pages/agent/components/agent-page-layout";
import { AgentRelationshipTile } from "@/pages/agent/components/agent-relationship-card";
import { AgentSection } from "@/pages/agent/components/agent-section";
import { AgentSurfaceNavigationActiveTab } from "@/pages/agent/components/agent-surface-navigation";
import { AgentProjectDetailCard } from "@/pages/agent/project/agent-project-detail-card";
import { AgentProjectBreadcrumbs } from "@/pages/agent/project/agent-project-breadcrumbs";
import { AgentProjectMetaCard } from "@/pages/agent/project/agent-project-meta-card";
import { buildProjectDetailViewModel } from "@/pages/agent/project/agent-project-page.utils";
import { AgentProjectTasksCard } from "@/pages/agent/project/agent-project-tasks-card";
import { buildTaskRows, getTaskRowsForView } from "@/pages/agent/tasks/agent-tasks-page.utils";
import { TaskView } from "@/pages/agent/tasks/use-agent-tasks-search-params";
import { useAgentIntentGraph } from "@/pages/agent/intent-graph/use-agent-intent-graph";
import { useAppStore } from "@/store";
import type { AssistantProjectObservable } from "@/store/assistant";
import {
  isClosedAssistantTaskStatus,
  normalizeAssistantTaskStatus,
} from "@/store/assistant/assistant-task-status";
import { AssistantTaskStatus } from "@/store/assistant/types";

interface AgentProjectPageProps {
  projectId: string;
}

export const AgentProjectPage = observer(({ projectId }: AgentProjectPageProps) => {
  const { store } = useAppStore();
  const project = store.assistantProjects.getCurrentAssistantModelById(projectId);
  const isMissing = store.assistantProjects.currentAssistantModelDoesNotExist(projectId);

  return (
    <AgentPageLayout activeTab={AgentSurfaceNavigationActiveTab.Projects}>
      {project ? (
        <ProjectDetail project={project} />
      ) : isMissing ? (
        <StyledNotFound color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
          Project not found.
        </StyledNotFound>
      ) : (
        <StyledLoadingCard aria-label="Loading project" variant={MDSCardVariant.Chrome}>
          <MDSSkeleton height={24} width="45%" />
          <MDSSkeleton height={16} width="75%" />
        </StyledLoadingCard>
      )}
    </AgentPageLayout>
  );
});

const ProjectDetail = observer(({ project }: { project: AssistantProjectObservable }) => {
  const { store } = useAppStore();
  const graph = useAgentIntentGraph();
  const projectDetail = buildProjectDetailViewModel({
    project,
    tasks: store.assistantTasks.currentAssistantModels,
    followUps: store.assistantFollowUps.currentAssistantModels,
  });
  const goalIds = new Set(
    (graph.data?.project_goal_links ?? [])
      .filter(link => link.project_id === project.id)
      .map(link => link.goal_id)
  );
  const goals = (graph.data?.goals ?? []).filter(item => goalIds.has(item.id));
  const parentProjectIds = (graph.data?.project_hierarchy_links ?? [])
    .filter(link => link.project_id === project.id)
    .map(link => link.parent_project_id);
  const parentProjects = parentProjectIds
    .map(parentProjectId => store.assistantProjects.getCurrentAssistantModelById(parentProjectId))
    .filter(item => item !== undefined);
  const parentProject = parentProjects[0];
  const childProjects = (graph.data?.project_hierarchy_links ?? [])
    .filter(link => link.parent_project_id === project.id)
    .map(link => store.assistantProjects.getCurrentAssistantModelById(link.project_id))
    .filter(item => item !== undefined);
  const tasks = store.assistantTasks.currentAssistantModels.filter(
    task => task.projectId === project.id
  );
  const taskRows = getTaskRowsForView({
    taskRows: buildTaskRows({
      tasks,
      projects: store.assistantProjects.currentAssistantModels,
      followUps: store.assistantFollowUps.currentAssistantModels,
      taskNoteReferences: store.assistantTaskNoteReferences.currentAssistantModels,
      getNoteById: store.notes.getNoteObservableById,
    }),
    view: TaskView.AllTasks,
  });
  const tasksLoaded =
    store.assistantTasks.currentAssistantModelsLoaded &&
    store.assistantProjects.currentAssistantModelsLoaded &&
    store.assistantFollowUps.currentAssistantModelsLoaded &&
    store.assistantTaskNoteReferences.currentAssistantModelsLoaded;
  const hasRelatedItems = parentProjects.length > 0 || childProjects.length > 0 || goals.length > 0;
  const openSourceNote = ({ noteId }: { noteId: string }) => {
    store.agentOverlay.openSourceNote({ noteId });
  };
  const toggleTaskComplete = async ({ taskId }: { taskId: string }) => {
    const task = store.assistantTasks.getCurrentAssistantModelById(taskId);
    if (!task) return;

    const currentStatus = normalizeAssistantTaskStatus({ status: task.status });
    const nextStatus = isClosedAssistantTaskStatus({ status: currentStatus })
      ? AssistantTaskStatus.Pending
      : AssistantTaskStatus.Completed;

    await task.updateStatus({ status: nextStatus });
  };

  return (
    <StyledPageStack>
      <AgentProjectBreadcrumbs
        parentProject={parentProject}
        project={project}
        onOpenProject={({ projectId }) => store.agentOverlay.openProject({ projectId })}
        onOpenProjects={() => store.agentOverlay.openProjects()}
      />
      <AgentProjectDetailCard
        projectDetail={projectDetail}
        onOpenActivities={({ projectId }) => {
          store.agentOverlay.openActivities({ kind: "project", projectId });
        }}
      />
      <AgentProjectTasksCard
        dataLoaded={tasksLoaded}
        taskRows={taskRows}
        onOpenActivities={({ taskId }) =>
          store.agentOverlay.openActivities({ kind: "task", taskId })
        }
        onOpenAllTasks={() => store.agentOverlay.openProjectTasks({ projectId: project.id })}
        onOpenNote={openSourceNote}
        onOpenProject={({ projectId }) => store.agentOverlay.openProject({ projectId })}
        onOpenTask={({ taskId }) => store.agentOverlay.openTask({ taskId })}
        onToggleTaskComplete={toggleTaskComplete}
      />

      {hasRelatedItems ? (
        <AgentSection
          description="Browse this project in the context of its larger outcomes and workstreams."
          title="Related"
        >
          <AgentHorizontalCardCarousel ariaLabel="Related">
            {parentProjects.map(item => (
              <AgentRelationshipTile
                key={item.id}
                description={item.description}
                icon={MDSIconKind.Folder}
                label="Parent project"
                title={item.title || "Untitled project"}
                onOpen={() => store.agentOverlay.openProject({ projectId: item.id })}
              />
            ))}
            {childProjects.map(childProject => (
              <AgentRelationshipTile
                key={childProject.id}
                description={childProject.description}
                icon={MDSIconKind.Folder}
                label="Subproject"
                title={childProject.title || "Untitled project"}
                onOpen={() => store.agentOverlay.openProject({ projectId: childProject.id })}
              />
            ))}
            {goals.map(goal => (
              <AgentRelationshipTile
                key={goal.id}
                description={goal.description}
                icon={MDSIconKind.Compass}
                label="Goal"
                title={goal.title || "Untitled goal"}
                onOpen={() => store.agentOverlay.openGoal({ goalId: goal.id })}
              />
            ))}
          </AgentHorizontalCardCarousel>
        </AgentSection>
      ) : null}

      <AgentSection title="Details">
        <AgentProjectMetaCard projectDetail={projectDetail} />
      </AgentSection>
    </StyledPageStack>
  );
});

const StyledPageStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
`;

const StyledNotFound = styled(MDSText)`
  padding: ${({ theme }) => theme.spacing[6]} 0;
`;

const StyledLoadingCard = styled(MDSCard)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;
